import {
  useInfiniteQuery,
  useQuery,
  type UseInfiniteQueryResult,
  type InfiniteData,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiDownload, apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

export interface AuditFilters {
  readonly action: string;
  readonly target: string;
  readonly actor: string;
  readonly tenant: string;
  readonly from: string;
  readonly to: string;
}

export const EMPTY_FILTERS: AuditFilters = {
  action: '',
  target: '',
  actor: '',
  tenant: '',
  from: '',
  to: '',
};

export interface AuditEntry {
  readonly id: string;
  readonly createdAt: string;
  readonly actorUserId: string | null;
  readonly actorEmail: string | null;
  readonly actorRole: string;
  readonly action: string;
  readonly targetType: string | null;
  readonly targetId: string | null;
  readonly tenantId: string | null;
  readonly ipAddress: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

export interface AuditPage {
  readonly entries: readonly AuditEntry[];
  readonly nextCursor: string | null;
}

export interface AuthEventSummary {
  readonly hours: number;
  readonly totals: Readonly<Record<string, number>>;
  readonly failedByIp: readonly { readonly key: string; readonly count: number }[];
  readonly failedByIdentifier: readonly { readonly key: string; readonly count: number }[];
}

export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface SecurityAlert {
  readonly code: string;
  readonly severity: AlertSeverity;
  readonly count: number;
  readonly since: string | null;
  readonly detail: Readonly<Record<string, unknown>>;
}

/** The filters as the query string the API expects: empty ones are left out, dates become full ISO instants. */
export function auditQuery(reason: string, filters: AuditFilters): URLSearchParams {
  const params = new URLSearchParams({ reason });
  if (filters.action.trim() !== '') params.set('action', filters.action.trim());
  if (filters.target.trim() !== '') params.set('target', filters.target.trim());
  if (filters.actor.trim() !== '') params.set('actor', filters.actor.trim());
  if (filters.tenant.trim() !== '') params.set('tenant', filters.tenant.trim());
  if (filters.from !== '') params.set('from', new Date(`${filters.from}T00:00:00`).toISOString());
  if (filters.to !== '') params.set('to', new Date(`${filters.to}T00:00:00`).toISOString());
  return params;
}

/**
 * `GET /api/v1/admin/security/audit` - the global journal, newest first, older pages by cursor. Audited with a stated reason (10
 * characters at least) typed by the person looking: the query stays disabled until they have. An applied filter set is part of the key,
 * so a new set starts again from the newest page.
 */
export function useAuditJournalQuery(
  reason: string,
  filters: AuditFilters,
): UseInfiniteQueryResult<InfiniteData<AuditPage, string | null>, ApiError> {
  const { accessToken } = useSession();
  return useInfiniteQuery({
    queryKey: ['security-audit', reason, filters],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = auditQuery(reason, filters);
      params.set('limit', '50');
      if (pageParam !== null) params.set('cursor', pageParam);
      return apiRequest<AuditPage>(`/admin/security/audit?${params.toString()}`, { accessToken });
    },
    getNextPageParam: (last) => last.nextCursor,
    enabled: accessToken !== null && reason.trim().length >= 10,
  });
}

/** `GET /api/v1/admin/security/audit/export` - the CSV (5,000 rows at most), the export is itself audited. */
export function downloadAuditExport(
  accessToken: string | null,
  reason: string,
  filters: AuditFilters,
): Promise<Blob> {
  return apiDownload(
    `/admin/security/audit/export?${auditQuery(reason, filters).toString()}`,
    accessToken,
  );
}

export function useAuthEventsQuery(
  reason: string,
  hours: number,
  active: boolean,
): UseQueryResult<AuthEventSummary, ApiError> {
  const { accessToken } = useSession();
  return useQuery({
    queryKey: ['security-auth-events', reason, hours],
    queryFn: () =>
      apiRequest<AuthEventSummary>(
        `/admin/security/auth-events?${new URLSearchParams({ reason, hours: String(hours) }).toString()}`,
        { accessToken },
      ),
    enabled: active && accessToken !== null && reason.trim().length >= 10,
  });
}

export function useAlertsQuery(
  reason: string,
  active: boolean,
): UseQueryResult<{ alerts: readonly SecurityAlert[] }, ApiError> {
  const { accessToken } = useSession();
  return useQuery({
    queryKey: ['security-alerts', reason],
    queryFn: () =>
      apiRequest<{ alerts: readonly SecurityAlert[] }>(
        `/admin/security/alerts?${new URLSearchParams({ reason }).toString()}`,
        { accessToken },
      ),
    enabled: active && accessToken !== null && reason.trim().length >= 10,
  });
}
